import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { TiptapAiEditor } from '@/tiptap/editor/ai-editor'
import { deserializeFromMarkdown } from '@/tiptap/markdown/deserializeFromMarkdown'
import {
  FileText,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  X,
  ExternalLink,
  Shield,
  Zap,
  Eye,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  SkipForward,
  Play,
  ArrowRight,
  Check,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { PolicyInlineQA } from '@/components/screenata/PolicyInlineQA'
import type { ClaimDisposition } from '@deepguide-ai/types'
import MainHeader from '@/components/layout/MainHeader'
import { Main } from '@/components/main'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/policies'
)({
  component: ScreenataPoliciesPage,
})

// ── Types ──

type GenerationItem = {
  slug: string
  title: string
  controlRefs: string[]
  priority: number
  category: 'policy' | 'deliverable'
  necessity: 'required' | 'common-ask' | 'scope-dependent' | 'optional'
  necessityReason: string | null
  evidenceEffort: 'low' | 'medium' | 'high' | null
  isGenerated: boolean
  claimCount: number
  docId: string | null
  templateId: string | null
}

type PolicyItem = {
  slug: string
  title: string
  controlRefs: string[]
  totalClaims: number
  verifiedClaims: number
  attestedClaims: number
  generated: boolean
  approvedAt: string | null
  approvedByName: string | null
  templateId: string | null
}

type TriageClaim = {
  id: string
  programId: string
  policyName: string
  claimType: string | null
  claimText: string
  systemOfRecord: string
  controlRef: string | null
  quoteText: string | null
  docBlockId: string | null
  confidence: string | null
  verification: string | null
  stale: boolean
  claimTupleKey: string | null
  claimFingerprint: string | null
  generationVersion: number
  matchConfidence: number | null
  needsReview: boolean
  disposition: ClaimDisposition | null
  dispositionAt: string | null
  dispositionBy: string | null
  dismissReason: string | null
  claimSource: string
  dismissable: boolean
  suggestedAction: ClaimDisposition | null
  suggestedReason: string | null
  controlEvidenceStatusId: string | null
  createdAt: string
}

type CompatibleTest = {
  id: string
  title: string
  status: string
  hasEvidence: boolean
}

type PolicyStatus =
  | 'not-started'
  | 'generating'
  | 'generated'
  | 'claims-pending'
  | 'ready'
  | 'skipped'

// ── Helpers ──

function derivePolicyStatus(
  item: GenerationItem,
  triageByPolicy: Record<string, { total: number; resolved: number }>,
  skippedSlugs: Set<string>,
): PolicyStatus {
  if (skippedSlugs.has(item.slug)) return 'skipped'
  if (!item.isGenerated) return 'not-started'

  const triage = triageByPolicy[item.slug]
  if (!triage || triage.total === 0) return 'generated'

  const allResolved = triage.resolved >= triage.total
  if (allResolved) return 'ready'
  return 'claims-pending'
}

// ── Badge Components ──

function PolicyStatusBadge({ status }: { status: PolicyStatus }) {
  switch (status) {
    case 'ready':
      return (
        <span className='inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700'>
          <CheckCircle2 className='h-3 w-3' />
          Ready
        </span>
      )
    case 'claims-pending':
      return (
        <span className='inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700'>
          <Clock className='h-3 w-3' />
          Claims Pending
        </span>
      )
    case 'generated':
      return (
        <span className='inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700'>
          Generated
        </span>
      )
    case 'generating':
      return (
        <span className='inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600'>
          <Loader2 className='h-3 w-3 animate-spin' />
          Generating
        </span>
      )
    case 'skipped':
      return (
        <span className='inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500'>
          Skipped
        </span>
      )
    default:
      return (
        <span className='inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
          Not Started
        </span>
      )
  }
}

function NecessityBadge({
  necessity,
  reason,
}: {
  necessity: string
  reason?: string | null
}) {
  if (necessity === 'required') return null
  if (necessity === 'common-ask') {
    return (
      <span className='shrink-0 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700'>
        Common Ask
      </span>
    )
  }
  if (necessity === 'scope-dependent') {
    return (
      <span
        className='shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700'
        title={reason ?? 'Depends on your scope'}
      >
        Scope-dependent
      </span>
    )
  }
  return (
    <span
      className='shrink-0 rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600'
      title={reason ?? 'Optional'}
    >
      Optional
    </span>
  )
}

// ── Main Page ──

function ScreenataPoliciesPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/screenata/policies',
  })
  const queryClient = useQueryClient()

  const itemsQuery = useQuery(
    trpc.complianceProgram.getGenerationItems.queryOptions({ framework: 'SOC2' })
  )
  const policiesQuery = useQuery(trpc.complianceProgram.getPolicies.queryOptions())
  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' })
  )

  const programId = (programQuery.data as any)?.id ?? ''

  const progressQuery = useQuery({
    ...trpc.claimTriage.progress.queryOptions({ programId }),
    enabled: !!programId,
    refetchInterval: 10_000,
  })

  const items: GenerationItem[] = ((itemsQuery.data as any)?.items ?? []) as GenerationItem[]
  const policies: PolicyItem[] = ((policiesQuery.data as any)?.policies ?? []) as PolicyItem[]
  const progressData = progressQuery.data as any
  const triageByPolicy: Record<string, { total: number; resolved: number }> =
    progressData?.byPolicy ?? {}
  const gateOpen: boolean = progressData?.gateOpen ?? false
  const totalUnresolved: number = progressData?.unresolved ?? 0

  // Skipped slugs from program
  const skippedPolicies: Array<{ slug: string; reason: string }> =
    ((programQuery.data as any)?.skippedPolicies ?? []) as Array<{
      slug: string
      reason: string
    }>
  const skippedSlugs = new Set(skippedPolicies.map((s) => s.slug))

  const policyBySlug = new Map(policies.map((p) => [p.slug, p]))

  // Sort all items by priority
  const sortedItems = [...items].sort((a, b) => a.priority - b.priority)

  // Stats
  const generatedCount = items.filter((i) => i.isGenerated).length
  const readyCount = sortedItems.filter(
    (i) => derivePolicyStatus(i, triageByPolicy, skippedSlugs) === 'ready'
  ).length
  const claimsResolved: number = progressData?.resolved ?? 0
  const claimsTotal: number = progressData?.total ?? 0

  const [viewingItem, setViewingItem] = useState<GenerationItem | null>(null)
  // Refresh gaps mutation
  const refreshGapsMutation = useMutation(
    trpc.claimTriage.refreshGaps.mutationOptions()
  )

  const handleRefreshGaps = () => {
    if (!programId) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(refreshGapsMutation.mutate as any)(
      { programId },
      {
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: trpc.claimTriage.progress.queryKey({ programId }),
          })
        },
      }
    )
  }

  if (itemsQuery.isLoading || policiesQuery.isLoading || programQuery.isLoading) {
    return (
      <div className='text-muted-foreground flex h-full items-center justify-center'>
        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
        Loading policies...
      </div>
    )
  }

  if (!programId) {
    return (
      <div className='text-muted-foreground flex h-full items-center justify-center'>
        No compliance program found. Start from the setup flow.
      </div>
    )
  }

  return (
    <Main>
      <MainHeader className='!mb-0 pr-4'>
        <span className='text-sm font-semibold'>Policies Hub</span>
      </MainHeader>

      <div className='h-[calc(100vh-50px)] w-full border-t'>
        <div className='h-full overflow-y-auto'>
          <div className='mx-auto w-full max-w-5xl space-y-6 p-6'>

            {/* Page title */}
            <div className='flex items-start justify-between gap-4'>
              <div>
                <h1 className='text-2xl font-semibold'>Policies</h1>
                <p className='text-muted-foreground text-sm'>
                  Generate policies, review claims, and unlock readiness.
                </p>
              </div>
            </div>

            {/* Stats row */}
            <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
              <div className='bg-card rounded-xl border p-4'>
                <div className='text-muted-foreground text-xs font-medium uppercase tracking-wide'>
                  Total Policies
                </div>
                <div className='mt-2 flex items-end gap-2'>
                  <div className='text-3xl font-semibold'>{items.length}</div>
                  <span className='text-muted-foreground mb-1 text-xs'>policies</span>
                </div>
              </div>
              <div className='bg-card rounded-xl border p-4'>
                <div className='text-muted-foreground text-xs font-medium uppercase tracking-wide'>
                  Generated
                </div>
                <div className='mt-2 flex items-end gap-2'>
                  <div className='text-3xl font-semibold text-blue-600'>
                    {generatedCount}
                  </div>
                  <span className='text-muted-foreground mb-1 text-xs'>
                    of {items.length}
                  </span>
                </div>
              </div>
              <div className='bg-card rounded-xl border p-4'>
                <div className='text-muted-foreground text-xs font-medium uppercase tracking-wide'>
                  Claims Resolved
                </div>
                <div className='mt-2 flex items-end gap-2'>
                  <div className='text-3xl font-semibold text-amber-600'>
                    {claimsResolved}
                  </div>
                  <span className='text-muted-foreground mb-1 text-xs'>
                    of {claimsTotal}
                  </span>
                </div>
              </div>
              <div className='bg-card rounded-xl border p-4'>
                <div className='text-muted-foreground text-xs font-medium uppercase tracking-wide'>
                  Ready
                </div>
                <div className='mt-2 flex items-end gap-2'>
                  <div className='text-3xl font-semibold text-emerald-600'>
                    {readyCount}
                  </div>
                  <span className='text-muted-foreground mb-1 text-xs'>policies</span>
                </div>
              </div>
            </div>

            {/* Gate banner */}
            {gateOpen ? (
              <div className='flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3'>
                <div className='flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 shrink-0 text-emerald-600' />
                  <p className='text-sm font-medium text-emerald-800'>
                    All claims resolved — you&apos;re ready to proceed to Readiness.
                  </p>
                </div>
                <Link
                  to='/workspaces/$workspaceId/screenata/readiness'
                  params={{ workspaceId }}
                  className='inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700'
                >
                  Continue to Readiness
                  <ArrowRight className='h-3.5 w-3.5' />
                </Link>
              </div>
            ) : totalUnresolved > 0 ? (
              <div className='flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3'>
                <div className='flex items-center gap-2'>
                  <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600' />
                  <p className='text-sm text-amber-800'>
                    <span className='font-semibold'>{totalUnresolved} claims unresolved</span>{' '}
                    across policies. Resolve all claims to unlock readiness.
                  </p>
                </div>
                <button
                  disabled
                  className='inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-gray-300 px-3 py-1.5 text-sm font-medium text-gray-500 cursor-not-allowed'
                >
                  Continue to Readiness
                  <ArrowRight className='h-3.5 w-3.5' />
                </button>
              </div>
            ) : null}

            {/* Coverage Gaps section */}
            <div className='flex items-center justify-between'>
              <div>
                <h2 className='text-sm font-semibold'>Coverage Gaps</h2>
                <p className='text-muted-foreground text-xs'>
                  TSC controls not covered by any policy claim.
                </p>
              </div>
              <button
                onClick={handleRefreshGaps}
                disabled={refreshGapsMutation.isPending}
                className='inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-gray-50 disabled:opacity-60'
              >
                {refreshGapsMutation.isPending ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <RefreshCw className='h-3.5 w-3.5' />
                )}
                Refresh Gaps
              </button>
            </div>

            {/* Policy list */}
            <div className='space-y-3'>
              <h2 className='text-lg font-semibold'>
                All Policies ({sortedItems.length})
              </h2>
              {sortedItems.length === 0 ? (
                <div className='text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm'>
                  No policies found.
                </div>
              ) : (
                <div className='bg-card overflow-hidden rounded-xl border'>
                  {sortedItems.map((item, idx) => {
                    const status = derivePolicyStatus(item, triageByPolicy, skippedSlugs)
                    const triage = triageByPolicy[item.slug]
                    return (
                      <div
                        key={item.slug}
                        className={`border-border/80 ${idx < sortedItems.length - 1 ? 'border-b' : ''}`}
                      >
                        <PolicyCard
                          item={item}
                          status={status}
                          triage={triage}
                          programId={programId}
                          skippedSlugs={skippedSlugs}
                          onOpen={() => setViewingItem(item)}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Policy detail panel */}
      {viewingItem && (
        <PolicyDetailPanel
          item={viewingItem}
          policyData={policyBySlug.get(viewingItem.slug)}
          workspaceId={workspaceId}
          programId={programId}
          onClose={() => setViewingItem(null)}
        />
      )}
    </Main>
  )
}

// ── Policy Card ──

function PolicyCard({
  item,
  status,
  triage,
  programId,
  skippedSlugs,
  onOpen,
}: {
  item: GenerationItem
  status: PolicyStatus
  triage?: { total: number; resolved: number }
  programId: string
  skippedSlugs: Set<string>
  onOpen: () => void
}) {
  const queryClient = useQueryClient()
  const [showSkipInput, setShowSkipInput] = useState(false)
  const [skipReason, setSkipReason] = useState('')

  const skipMutation = useMutation(trpc.claimTriage.skipPolicy.mutationOptions())
  const unskipMutation = useMutation(trpc.claimTriage.unskipPolicy.mutationOptions())

  const isSkipped = skippedSlugs.has(item.slug)

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getGenerationItems.queryKey({ framework: 'SOC2' }),
    })
  }

  const handleSkip = () => {
    if (!skipReason.trim()) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(skipMutation.mutate as any)(
      { programId, slug: item.slug, reason: skipReason },
      {
        onSuccess: () => {
          setShowSkipInput(false)
          setSkipReason('')
          invalidate()
        },
      }
    )
  }

  const handleUnskip = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(unskipMutation.mutate as any)(
      { programId, slug: item.slug },
      { onSuccess: invalidate }
    )
  }

  return (
    <div
      onClick={!isSkipped ? onOpen : undefined}
      className={`px-4 py-3 transition-colors ${
        !isSkipped ? 'cursor-pointer hover:bg-gray-50' : ''
      } ${isSkipped ? 'opacity-60' : ''}`}
    >
      <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
        {/* Left: title + badges */}
        <div className='flex min-w-0 flex-1 flex-wrap items-start gap-2'>
          <FileText
            className={`mt-0.5 h-4 w-4 shrink-0 ${
              status === 'ready'
                ? 'text-emerald-600'
                : status === 'not-started' || isSkipped
                  ? 'text-gray-400'
                  : 'text-blue-500'
            }`}
          />
          <h3 className='min-w-0 flex-1 text-sm font-medium leading-snug'>
            {item.title}
          </h3>
          <NecessityBadge necessity={item.necessity} reason={item.necessityReason} />
        </div>

        {/* Right: status + actions */}
        <div
          className='flex shrink-0 flex-wrap items-center gap-1.5'
          onClick={(e) => e.stopPropagation()}
        >
          <PolicyStatusBadge status={status} />

          {/* Triage progress pill */}
          {triage && triage.total > 0 && (
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                triage.resolved >= triage.total
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-700'
              }`}
            >
              {triage.resolved}/{triage.total} resolved
            </span>
          )}

          {/* Actions */}
          {status === 'not-started' && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onOpen() }}
                className='inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700'
              >
                <Play className='h-3 w-3' />
                Generate
              </button>
              {!showSkipInput && (
                <button
                  onClick={() => setShowSkipInput(true)}
                  className='inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100'
                >
                  <SkipForward className='h-3 w-3' />
                  Skip
                </button>
              )}
            </>
          )}

          {isSkipped && (
            <button
              onClick={handleUnskip}
              disabled={unskipMutation.isPending}
              className='inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-60'
            >
              Undo Skip
            </button>
          )}

          {item.isGenerated && !isSkipped && (
            <button
              onClick={onOpen}
              className='inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100'
            >
              <ChevronRight className='h-3 w-3' />
              Open
            </button>
          )}
        </div>
      </div>

      {/* Control refs */}
      <div className='mt-1.5 flex flex-wrap gap-1 pl-6'>
        {item.controlRefs.map((ref) => (
          <span
            key={ref}
            className={`inline-block rounded px-1.5 py-0.5 font-mono text-xs ${
              item.isGenerated
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border border-gray-200 bg-gray-50 text-gray-500'
            }`}
          >
            {ref}
          </span>
        ))}
      </div>

      {/* Skip input */}
      {showSkipInput && (
        <div
          className='mt-2 pl-6'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex items-center gap-2'>
            <input
              type='text'
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              placeholder='Reason for skipping...'
              className='flex-1 rounded border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400'
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSkip()
                if (e.key === 'Escape') {
                  setShowSkipInput(false)
                  setSkipReason('')
                }
              }}
              autoFocus
            />
            <button
              onClick={handleSkip}
              disabled={!skipReason.trim() || skipMutation.isPending}
              className='inline-flex items-center gap-1 rounded bg-amber-500 px-2 py-1 text-xs font-medium text-white disabled:opacity-50'
            >
              {skipMutation.isPending ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                <Check className='h-3 w-3' />
              )}
              Confirm
            </button>
            <button
              onClick={() => {
                setShowSkipInput(false)
                setSkipReason('')
              }}
              className='rounded p-1 text-gray-400 hover:bg-gray-100'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Policy Detail Panel (dialog) ──

function PolicyDetailPanel({
  item,
  policyData,
  workspaceId,
  programId,
  onClose,
}: {
  item: GenerationItem
  policyData?: PolicyItem
  workspaceId: string
  programId: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [initialContent, setInitialContent] = useState<any>(null)
  const [contentReady, setContentReady] = useState(false)
  const [showClaims, setShowClaims] = useState(true)
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null)

  // Q&A → Generate state machine for ungenerated policies
  const [isGenerating, setIsGenerating] = useState(false)

  const generateMutation = useMutation(
    trpc.complianceProgram.startPolicyGeneration.mutationOptions()
  )

  // Poll for generation completion
  const generationItemsQuery = useQuery({
    ...trpc.complianceProgram.getGenerationItems.queryOptions({ framework: 'SOC2' }),
    enabled: isGenerating,
    refetchInterval: isGenerating ? 5_000 : false,
  })

  // Detect when generation completes
  useEffect(() => {
    if (!isGenerating) return
    const updatedItems: GenerationItem[] = ((generationItemsQuery.data as any)?.items ?? []) as GenerationItem[]
    const updated = updatedItems.find(i => i.slug === item.slug)
    if (updated?.isGenerated) {
      setIsGenerating(false)
      setContentReady(false)
      // Refresh parent data
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getGenerationItems.queryKey({ framework: 'SOC2' }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicies.queryKey(),
      })
      // Invalidate doc content query — the updated item may have a different docId
      if (updated?.docId) {
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({ docId: updated.docId }),
        })
      }
    }
  }, [generationItemsQuery.data, isGenerating])

  const triggerGeneration = () => {
    setIsGenerating(true)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(generateMutation.mutate as any)(
      { programId, selectedSlugs: [item.slug] },
    )
  }

  // Determine if the policy has been generated (either initially or after we triggered it)
  const generatedItem = isGenerating ? null : (() => {
    const updatedItems: GenerationItem[] = ((generationItemsQuery.data as any)?.items ?? []) as GenerationItem[]
    const updated = updatedItems.find(i => i.slug === item.slug)
    return (updated?.isGenerated || item.isGenerated) ? (updated ?? item) : null
  })()
  const showDocViewer = !!generatedItem

  const claimsSidebarRef = useRef<HTMLDivElement>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)

  // Use the generatedItem's docId (may be updated after generation) or fall back to item's
  const effectiveDocId = generatedItem?.docId ?? item.docId
  const docQuery = useQuery({
    ...trpc.complianceProgram.getPolicyDocContent.queryOptions({
      docId: effectiveDocId ?? '',
    }),
    enabled: !!effectiveDocId && showDocViewer,
  })

  const claimsQuery = useQuery({
    ...trpc.claimTriage.list.queryOptions({
      programId,
      policyName: item.slug,
    }),
    enabled: !!programId,
  })

  const suggestMutation = useMutation(trpc.claimTriage.suggest.mutationOptions())
  const bulkResolveMutation = useMutation(trpc.claimTriage.bulkResolve.mutationOptions())

  const claims: TriageClaim[] = (claimsQuery.data ?? []) as TriageClaim[]
  const unresolvedClaims = claims.filter(
    (c) => c.disposition === null || c.needsReview
  )
  const resolvedCount = claims.length - unresolvedClaims.length

  // Convert doc content
  useEffect(() => {
    if (!docQuery.data || contentReady) return
    const docData = docQuery.data as any
    const { plateContent, content } = docData as { plateContent: any; content: string }
    const pcContent = (plateContent as any)?.content
    const isSingleEmptyParagraph =
      Array.isArray(pcContent) &&
      pcContent.length === 1 &&
      pcContent[0]?.type === 'paragraph' &&
      (!pcContent[0]?.content || pcContent[0].content.length === 0)
    const hasPlateContent =
      plateContent &&
      typeof plateContent === 'object' &&
      Array.isArray(pcContent) &&
      pcContent.length > 0 &&
      !isSingleEmptyParagraph

    if (hasPlateContent) {
      setInitialContent(plateContent)
      setContentReady(true)
      return
    }

    if (content && content.length > 10) {
      try {
        const result = deserializeFromMarkdown(content)
        setInitialContent(result.content)
        setContentReady(true)
      } catch {
        setInitialContent({ type: 'doc', content: [] })
        setContentReady(true)
      }
      return
    }

    setInitialContent({ type: 'doc', content: [] })
    setContentReady(true)
  }, [docQuery.data, contentReady, item.docId])

  // Legacy comment→claim cross-highlighting support
  const commentToClaimMap = (claimsQuery.data as any)?.commentToClaimMap ?? {}

  useEffect(() => {
    const container = editorContainerRef.current
    if (!container || Object.keys(commentToClaimMap).length === 0) return

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const commentSpan = target.closest('span[data-comment-id]') as HTMLElement | null
      container.querySelectorAll('span.comment-active').forEach((el) => {
        el.classList.remove('comment-active')
      })
      if (!commentSpan) {
        setSelectedClaimId(null)
        return
      }
      const commentId = commentSpan.getAttribute('data-comment-id')
      if (!commentId) return
      const claimId = commentToClaimMap[commentId]
      if (claimId) {
        setSelectedClaimId(claimId)
        commentSpan.classList.add('comment-active')
        requestAnimationFrame(() => {
          const claimEl = claimsSidebarRef.current?.querySelector(
            `[data-claim-id="${claimId}"]`
          )
          claimEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
      }
    }

    container.addEventListener('click', handleClick)
    return () => container.removeEventListener('click', handleClick)
  }, [commentToClaimMap])

  const handleClaimClick = (claimId: string) => {
    setSelectedClaimId(claimId)
    const commentId = Object.entries(commentToClaimMap).find(
      ([, cId]) => cId === claimId
    )?.[0]
    if (!commentId || !editorContainerRef.current) return
    const commentSpan = editorContainerRef.current.querySelector(
      `span[data-comment-id="${commentId}"]`
    ) as HTMLElement | null
    if (commentSpan) {
      commentSpan.scrollIntoView({ behavior: 'smooth', block: 'center' })
      commentSpan.classList.add('comment-active')
      setTimeout(() => commentSpan.classList.remove('comment-active'), 2000)
    }
  }

  const invalidateClaims = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.claimTriage.list.queryKey({ programId, policyName: item.slug }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.claimTriage.progress.queryKey({ programId }),
    })
  }

  const handleSuggest = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(suggestMutation.mutate as any)(
      { programId, policyName: item.slug },
      { onSuccess: invalidateClaims }
    )
  }

  const handleAcceptAllSuggestions = () => {
    const suggestedDispositions = claims
      .filter(
        (c) =>
          (c.disposition === null || c.needsReview) &&
          c.suggestedAction !== null
      )
      .map((c) => ({
        claimId: c.id,
        disposition: c.suggestedAction!,
        ...(c.suggestedAction === 'LINK_EXISTING' && c.controlEvidenceStatusId
          ? { controlEvidenceStatusId: c.controlEvidenceStatusId }
          : {}),
      }))

    if (suggestedDispositions.length === 0) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(bulkResolveMutation.mutate as any)(
      { programId, dispositions: suggestedDispositions },
      { onSuccess: invalidateClaims }
    )
  }

  const hasContent = contentReady && initialContent?.content?.length > 0

  return (
    <div className='fixed inset-0 z-50 flex'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/40' onClick={onClose} />

      {/* Panel */}
      <div className='relative ml-auto flex h-full w-full max-w-6xl flex-col bg-white shadow-xl'>
        {/* Header */}
        <div className='flex shrink-0 items-center justify-between border-b px-6 py-4'>
          <div className='flex min-w-0 items-center gap-3'>
            <FileText className='h-5 w-5 shrink-0 text-blue-600' />
            <h2 className='truncate text-lg font-semibold'>{item.title}</h2>
            {policyData?.approvedAt && (
              <span className='inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700'>
                <CheckCircle2 className='h-3.5 w-3.5' />
                Approved{policyData.approvedByName ? ` by ${policyData.approvedByName}` : ''}
              </span>
            )}
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            {claims.length > 0 && (
              <button
                onClick={() => setShowClaims((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors ${
                  showClaims
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-muted-foreground hover:text-foreground hover:bg-gray-100'
                }`}
              >
                <Shield className='h-3.5 w-3.5' />
                Claims ({claims.length})
              </button>
            )}
            <span className='inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-600'>
              Read only
            </span>
            {item.templateId && (
              <Link
                to='/workspaces/$workspaceId/templates/$templateId'
                params={{ workspaceId, templateId: item.templateId }}
                className='text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-xs'
              >
                <ExternalLink className='h-3.5 w-3.5' />
                Template
              </Link>
            )}
            <button
              onClick={onClose}
              className='text-muted-foreground rounded-lg p-1.5 hover:bg-gray-100'
            >
              <X className='h-5 w-5' />
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className='flex flex-1 overflow-hidden'>
          {/* Editor / Q&A flow */}
          <div ref={editorContainerRef} className='flex-1 overflow-y-auto'>
            {/* Q&A → Generate flow for ungenerated policies */}
            {!showDocViewer && !isGenerating && (
              <div className='flex h-full flex-col items-center justify-center px-6 py-12'>
                <div className='w-full max-w-lg space-y-6'>
                  <div className='text-center'>
                    <FileText className='mx-auto h-10 w-10 text-gray-300' />
                    <h3 className='mt-3 text-lg font-semibold'>{item.title}</h3>
                    <p className='text-muted-foreground mt-1 text-sm'>
                      Answer a few questions to generate a tailored policy for your organization.
                    </p>
                  </div>

                  <div className='rounded-xl border bg-white shadow-sm'>
                    <PolicyInlineQA
                      programId={programId}
                      policySlug={item.slug}
                      policyTitle={item.title}
                      existingQA={null}
                      onComplete={() => {
                        triggerGeneration()
                      }}
                      onSkip={() => {
                        triggerGeneration()
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Generating spinner */}
            {isGenerating && (
              <div className='flex h-full flex-col items-center justify-center px-6 py-12'>
                <Loader2 className='h-8 w-8 animate-spin text-emerald-600' />
                <h3 className='mt-4 text-lg font-semibold'>Generating {item.title}</h3>
                <p className='text-muted-foreground mt-1 text-sm'>
                  This may take a minute. The AI is writing your policy and creating evidence claims...
                </p>
              </div>
            )}

            {/* Doc viewer for generated policies */}
            {showDocViewer && (
              <>
                {(docQuery.isLoading || !contentReady) && (
                  <div className='flex items-center justify-center py-12'>
                    <Loader2 className='h-6 w-6 animate-spin text-blue-600' />
                  </div>
                )}
                {contentReady && !hasContent && (
                  <div className='text-muted-foreground px-6 py-12 text-center'>
                    <p>Document is empty.</p>
                    <p className='mt-1 text-sm'>
                      The policy template exists but has no content yet.
                    </p>
                  </div>
                )}
                {contentReady && hasContent && (
                  <div className='h-full'>
                    <TiptapAiEditor
                      content={initialContent}
                      readOnly={true}
                      enableComments={true}
                      docId={effectiveDocId ?? undefined}
                      workspaceId={workspaceId}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Claims triage sidebar */}
          {showClaims && claims.length > 0 && (
            <div
              ref={claimsSidebarRef}
              className='flex w-96 shrink-0 flex-col overflow-hidden border-l bg-gray-50/50'
            >
              {/* Sidebar header */}
              <div className='sticky top-0 z-10 border-b bg-white p-4'>
                <div className='flex items-center justify-between'>
                  <h3 className='flex items-center gap-1.5 text-sm font-semibold'>
                    <Shield className='h-4 w-4 text-blue-600' />
                    Claims ({claims.length})
                  </h3>
                  <button
                    onClick={() => setShowClaims(false)}
                    className='text-muted-foreground rounded p-1 hover:bg-gray-100'
                  >
                    <X className='h-3.5 w-3.5' />
                  </button>
                </div>

                {/* Progress */}
                <div className='mt-2'>
                  <div className='flex items-center justify-between text-xs'>
                    <span className='text-muted-foreground'>
                      {resolvedCount} of {claims.length} resolved
                    </span>
                    {unresolvedClaims.length > 0 && (
                      <span className='font-medium text-amber-600'>
                        {unresolvedClaims.length} pending
                      </span>
                    )}
                  </div>
                  <div className='mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100'>
                    <div
                      className='h-1.5 bg-emerald-500 transition-all'
                      style={{
                        width:
                          claims.length > 0
                            ? `${(resolvedCount / claims.length) * 100}%`
                            : '0%',
                      }}
                    />
                  </div>
                </div>

                {/* Bulk actions */}
                <div className='mt-2 flex gap-2'>
                  <button
                    onClick={handleSuggest}
                    disabled={suggestMutation.isPending}
                    className='inline-flex flex-1 items-center justify-center gap-1.5 rounded border px-2 py-1.5 text-xs font-medium transition-colors hover:bg-gray-50 disabled:opacity-60'
                  >
                    {suggestMutation.isPending ? (
                      <Loader2 className='h-3 w-3 animate-spin' />
                    ) : (
                      <Zap className='h-3 w-3' />
                    )}
                    Auto-Suggest
                  </button>
                  {claims.some(
                    (c) => (c.disposition === null || c.needsReview) && c.suggestedAction
                  ) && (
                    <button
                      onClick={handleAcceptAllSuggestions}
                      disabled={bulkResolveMutation.isPending}
                      className='inline-flex flex-1 items-center justify-center gap-1.5 rounded bg-emerald-600 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-60'
                    >
                      {bulkResolveMutation.isPending ? (
                        <Loader2 className='h-3 w-3 animate-spin' />
                      ) : (
                        <Check className='h-3 w-3' />
                      )}
                      Accept All
                    </button>
                  )}
                </div>
              </div>

              {/* Claim cards */}
              <div className='flex-1 space-y-2 overflow-y-auto p-3'>
                {claims.map((claim) => (
                  <TriageClaimCard
                    key={claim.id}
                    claim={claim}
                    isSelected={selectedClaimId === claim.id}
                    onClick={() => handleClaimClick(claim.id)}
                    workspaceId={workspaceId}
                    onResolved={invalidateClaims}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='shrink-0 border-t bg-gray-50 px-6 py-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-muted-foreground text-xs'>Controls:</span>
            {item.controlRefs.map((ref) => (
              <span
                key={ref}
                className='inline-block rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-mono text-xs text-blue-700'
              >
                {ref}
              </span>
            ))}
            {item.claimCount > 0 && (
              <span className='text-muted-foreground ml-auto text-xs'>
                {item.claimCount} claims
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Triage Claim Card ──

function TriageClaimCard({
  claim,
  isSelected,
  onClick,
  workspaceId,
  onResolved,
}: {
  claim: TriageClaim
  isSelected?: boolean
  onClick?: () => void
  workspaceId: string
  onResolved: () => void
}) {
  const [showDismissInput, setShowDismissInput] = useState(false)
  const [dismissReason, setDismissReason] = useState('')
  const [showLinkPicker, setShowLinkPicker] = useState(false)

  const resolveMutation = useMutation(trpc.claimTriage.resolve.mutationOptions())

  const compatibleTestsQuery = useQuery({
    ...trpc.claimTriage.compatibleTests.queryOptions({ claimId: claim.id }),
    enabled: showLinkPicker,
  })

  const compatibleTests: CompatibleTest[] = (compatibleTestsQuery.data ?? []) as CompatibleTest[]

  const isResolved = claim.disposition !== null && !claim.needsReview

  const handleDispose = (
    disposition: ClaimDisposition,
    cesId?: string,
    reason?: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(resolveMutation.mutate as any)(
      {
        claimId: claim.id,
        disposition,
        dismissReason: reason,
        controlEvidenceStatusId: cesId,
      },
      {
        onSuccess: () => {
          setShowDismissInput(false)
          setDismissReason('')
          setShowLinkPicker(false)
          onResolved()
        },
      }
    )
  }

  return (
    <div
      data-claim-id={claim.id}
      onClick={onClick}
      className={`cursor-pointer space-y-2 rounded-lg border bg-white p-3 text-xs shadow-sm transition-all ${
        isSelected ? 'border-blue-400 ring-2 ring-blue-300' : 'hover:border-blue-200'
      } ${isResolved ? 'opacity-75' : ''}`}
    >
      {/* Top row: controlRef + disposition badge */}
      <div className='flex items-start justify-between gap-2'>
        <div className='flex items-center gap-1.5'>
          {claim.controlRef && (
            <span className='rounded border border-blue-200 bg-blue-50 px-1 py-0.5 font-mono text-[10px] text-blue-700'>
              {claim.controlRef}
            </span>
          )}
          {claim.claimSource === 'TSC_GAP' && (
            <span className='rounded border border-red-200 bg-red-50 px-1 py-0.5 text-[10px] text-red-600'>
              Gap
            </span>
          )}
          <VerificationIcon verification={claim.verification} />
        </div>
        <DispositionBadge disposition={claim.disposition} needsReview={claim.needsReview} />
      </div>

      {/* Claim text */}
      <p className='leading-relaxed text-gray-700'>{claim.claimText}</p>

      {/* Metadata row */}
      <div className='text-muted-foreground flex flex-wrap items-center gap-2 border-t pt-1.5 text-[10px]'>
        {claim.systemOfRecord && (
          <span className='rounded bg-gray-100 px-1.5 py-0.5'>{claim.systemOfRecord}</span>
        )}
        {claim.confidence != null && (
          <span className='capitalize'>{claim.confidence} conf.</span>
        )}
        {claim.suggestedAction && !isResolved && (
          <span className='font-medium text-blue-600'>
            Suggested: {formatDisposition(claim.suggestedAction)}
          </span>
        )}
        {claim.controlEvidenceStatusId && (
          <Link
            to='/workspaces/$workspaceId/compliance/tests/$testId'
            params={{ workspaceId, testId: claim.controlEvidenceStatusId }}
            search={{ from: 'screenata-tests' as any }}
            onClick={(e) => e.stopPropagation()}
            className='ml-auto flex items-center gap-1 text-blue-600 hover:underline'
          >
            <ExternalLink className='h-3 w-3' />
            View test
          </Link>
        )}
      </div>

      {/* Action buttons (only for unresolved) */}
      {!isResolved && (
        <div
          className='flex flex-wrap gap-1.5 border-t pt-2'
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleDispose('CREATE_TEST')}
            disabled={resolveMutation.isPending}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors disabled:opacity-50 ${
              claim.suggestedAction === 'CREATE_TEST'
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Zap className='h-2.5 w-2.5' />
            Create Test
          </button>

          <button
            onClick={() => {
              setShowLinkPicker((v) => !v)
              setShowDismissInput(false)
            }}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${
              claim.suggestedAction === 'LINK_EXISTING'
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <ExternalLink className='h-2.5 w-2.5' />
            Link Existing
          </button>

          {claim.dismissable && (
            <button
              onClick={() => {
                setShowDismissInput((v) => !v)
                setShowLinkPicker(false)
              }}
              className='inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-200'
            >
              <XCircle className='h-2.5 w-2.5' />
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* Dismiss textarea */}
      {showDismissInput && (
        <div className='space-y-1.5' onClick={(e) => e.stopPropagation()}>
          <textarea
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder='Reason for dismissing...'
            rows={2}
            className='w-full rounded border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400'
          />
          <div className='flex gap-1.5'>
            <button
              onClick={() =>
                handleDispose('DISMISS', undefined, dismissReason)
              }
              disabled={!dismissReason.trim() || resolveMutation.isPending}
              className='inline-flex items-center gap-1 rounded bg-gray-700 px-2 py-1 text-[10px] font-medium text-white disabled:opacity-50'
            >
              {resolveMutation.isPending ? (
                <Loader2 className='h-2.5 w-2.5 animate-spin' />
              ) : null}
              Dismiss
            </button>
            <button
              onClick={() => setShowDismissInput(false)}
              className='rounded px-2 py-1 text-[10px] text-gray-500 hover:bg-gray-100'
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Link existing picker */}
      {showLinkPicker && (
        <div className='space-y-1.5' onClick={(e) => e.stopPropagation()}>
          {compatibleTestsQuery.isLoading && (
            <div className='flex items-center gap-1.5 text-[10px] text-gray-500'>
              <Loader2 className='h-3 w-3 animate-spin' />
              Loading compatible tests...
            </div>
          )}
          {!compatibleTestsQuery.isLoading && compatibleTests.length === 0 && (
            <p className='text-[10px] text-gray-500'>
              No compatible tests found.
            </p>
          )}
          {compatibleTests.length > 0 && (
            <div className='max-h-32 space-y-1 overflow-y-auto rounded border bg-white p-1'>
              {compatibleTests.map((test) => (
                <button
                  key={test.id}
                  onClick={() => handleDispose('LINK_EXISTING', test.id)}
                  disabled={resolveMutation.isPending}
                  className='flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-[10px] hover:bg-blue-50 disabled:opacity-50'
                >
                  <span className='truncate font-medium text-gray-700'>
                    {test.title}
                  </span>
                  <span
                    className={`ml-2 shrink-0 rounded px-1 py-0.5 text-[9px] font-medium ${
                      test.hasEvidence
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {test.status}
                  </span>
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => setShowLinkPicker(false)}
            className='text-[10px] text-gray-500 hover:text-gray-700'
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

// ── Small helpers ──

function formatDisposition(d: ClaimDisposition): string {
  if (d === 'CREATE_TEST') return 'Create Test'
  if (d === 'LINK_EXISTING') return 'Link Existing'
  return 'Dismiss'
}

function DispositionBadge({
  disposition,
  needsReview,
}: {
  disposition: ClaimDisposition | null
  needsReview: boolean
}) {
  if (needsReview) {
    return (
      <span className='inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700'>
        <AlertTriangle className='h-2.5 w-2.5' />
        Review
      </span>
    )
  }
  if (!disposition) return null

  if (disposition === 'CREATE_TEST') {
    return (
      <span className='inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700'>
        <Zap className='h-2.5 w-2.5' />
        Create Test
      </span>
    )
  }
  if (disposition === 'LINK_EXISTING') {
    return (
      <span className='inline-flex items-center gap-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700'>
        <ExternalLink className='h-2.5 w-2.5' />
        Linked
      </span>
    )
  }
  return (
    <span className='inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500'>
      <XCircle className='h-2.5 w-2.5' />
      Dismissed
    </span>
  )
}

function VerificationIcon({ verification }: { verification: string | null }) {
  if (!verification) return null
  if (
    verification.toLowerCase().includes('code') ||
    verification.toLowerCase().includes('scan')
  ) {
    return (
      <span title='Code/scan verified'>
        <Zap className='h-3 w-3 text-blue-500' />
      </span>
    )
  }
  if (
    verification.toLowerCase().includes('config') ||
    verification.toLowerCase().includes('log')
  ) {
    return (
      <span title='Config/log verified'>
        <Eye className='h-3 w-3 text-purple-500' />
      </span>
    )
  }
  return (
    <span title={verification}>
      <Shield className='h-3 w-3 text-gray-400' />
    </span>
  )
}
